import React, { useState, useEffect, useCallback } from 'react';
import { 
  NewspaperIcon,
  ClockIcon,
  ArrowTopRightOnSquareIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  RssIcon,
  CloudIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../contexts/LanguageContext';
import { getApiUrl } from '../utils/api';

interface NewsArticle {
  source: {
    id: string | null;
    name: string;
  };
  author: string | null;
  title: string;
  description: string;
  url: string;
  urlToImage: string | null;
  publishedAt: string;
  content: string | null;
  category?: string;
  aiSummary?: string;
}

interface NewsResponse {
  status: string;
  totalResults: number;
  articles: NewsArticle[];
  language: string;
  page: number;
  totalPages: number;
  sources: {
    rss: number;
    api: number;
  };
  updateInterval: number;
  error?: string;
  message?: string;
}

const RealTimeNews: React.FC = () => {
  const { language } = useLanguage();
  const [news, setNews] = useState<NewsArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [newsInfo, setNewsInfo] = useState({
    language: 'korean',
    sources: { rss: 0, api: 0 },
    updateInterval: 15
  });
  
  // 페이지네이션 상태 추가
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  // 설정에서 뉴스 업데이트 주기 가져오기
  const [settings, setSettings] = useState<any>(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settingsUrl = `${getApiUrl()}/api/settings`;
        console.log('📰 설정 로드 시도:', settingsUrl);
        
        const response = await fetch(settingsUrl);
        if (response.ok) {
          const settings = await response.json();
          console.log('📰 설정 로드 성공:', settings);
          
          if (settings.newsLanguage && settings.newsLanguage !== language) {
            setNewsInfo({
              language: settings.newsLanguage,
              sources: settings.sources || { rss: 0, api: 0 },
              updateInterval: settings.updateInterval || 15
            });
            console.log('📰 뉴스 언어 설정:', settings.newsLanguage);
          }
        } else {
          console.error('📰 설정 로드 실패:', response.status);
        }
      } catch (error) {
        console.error('📰 설정 로드 오류:', error);
      }
    };
    
    fetchSettings();
  }, [language]); // 빈 의존성 배열로 한 번만 실행

  const fetchNews = useCallback(async (showLoading = true, page = 1, append = false) => {
    if (showLoading && !append) setLoading(true);
    if (append) setLoadingMore(true);
    setError(null);

    try {
      // 언어 설정에 따라 뉴스 언어 결정 (브라우저 언어도 고려)
      const browserLanguage = navigator.language || navigator.languages?.[0] || 'en';
      const isKoreanUser = language === 'ko' || browserLanguage.startsWith('ko');
      const newsLanguage = isKoreanUser ? 'korean' : 'english';
      
      console.log(`📰 뉴스 요청: 언어=${newsLanguage}, 페이지=${page}`);
      
      // getApiUrl() 함수 사용으로 변경
      const serverUrl = `${getApiUrl()}/api/news?language=${newsLanguage}&page=${page}&limit=10`;
      
      console.log(`📰 서버 요청 URL: ${serverUrl}`);
      
      const response = await fetch(serverUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        // 타임아웃 설정
        signal: AbortSignal.timeout(10000) // 10초로 단축
      });

      if (!response.ok) {
        throw new Error(`서버 응답 오류: ${response.status} ${response.statusText}`);
      }

      const data: NewsResponse = await response.json();

      if (data.status === 'ok' && data.articles && Array.isArray(data.articles)) {
        // 올바른 URL 가진 뉴스만 필터링
        const validatedArticles = data.articles.filter((article: NewsArticle) => {
          return article.url && 
                 article.url !== '#' && 
                 article.url.startsWith('http') &&
                 article.title && 
                 article.title.trim() !== '' &&
                 article.title !== 'No title';
        });

        if (append) {
          // 페이지 추가 로딩인 경우 기존 뉴스에 추가
          setNews(prev => [...prev, ...validatedArticles]);
        } else {
          // 새로고침이나 초기 로딩인 경우에도 기존 뉴스를 유지하면서 새로운 뉴스 추가
          setNews(prev => {
            // 기존 뉴스의 URL들을 Set으로 만들어 중복 체크
            const existingUrls = new Set(prev.map(article => article.url));
            
            // 새로운 뉴스 중 중복되지 않는 것들만 필터링
            const newUniqueArticles = validatedArticles.filter(article => 
              !existingUrls.has(article.url)
            );
            
            // 새로운 뉴스를 맨 앞에 추가하고, 전체 개수가 100개를 초과하면 뒤에서 제거
            const updatedNews = [...newUniqueArticles, ...prev];
            return updatedNews.slice(0, 100); // 최대 100개 유지
          });
        }
        
        setCurrentPage(data.page || page);
        setTotalPages(data.totalPages || 1);
        setTotalResults(data.totalResults || validatedArticles.length);
        setNewsInfo({
          language: data.language || newsLanguage,
          sources: data.sources || { rss: 0, api: 0 },
          updateInterval: data.updateInterval || 15
        });
        setLastUpdated(new Date());
        console.log(`📰 뉴스 ${validatedArticles.length}개 로드 성공 (페이지 ${page}/${data.totalPages}) - 기존 리스트 유지`);
        
        if (data.error) {
          console.warn('📰 뉴스 경고:', data.error);
        }
      } else {
        throw new Error(data.message || '뉴스 데이터를 찾을 수 없습니다');
      }
    } catch (error) {
      console.error('📰 뉴스 가져오기 실패:', error);
      
      let errorMessage = '뉴스를 불러올 수 없습니다';
      
      if (error instanceof Error) {
        if (error.name === 'AbortError' || error.message.includes('timeout')) {
          errorMessage = '서버 응답 시간 초과. 잠시 후 다시 시도해주세요.';
        } else if (error.message.includes('fetch') || error.message.includes('NetworkError')) {
          errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
        } else {
          errorMessage = error.message;
        }
      }
      
      setError(errorMessage);
      
      // 오류 발생 시에도 기존 뉴스 리스트는 유지
    } finally {
      if (showLoading && !append) setLoading(false);
      if (append) setLoadingMore(false);
    }
  }, [language]); // 의존성 최소화

  // 초기 뉴스 로딩
  useEffect(() => {
    fetchNews();
  }, [fetchNews]);

  // 별도 useEffect로 뉴스 업데이트 주기 관리
  useEffect(() => {
    // 설정된 주기에 따라 뉴스 업데이트 (최소 5분)
    const configuredInterval = settings?.newsUpdateInterval || 15;
    const updateInterval = Math.max(configuredInterval, 5) * 60 * 1000; // 최소 5분
    
    console.log(`📰 뉴스 업데이트 주기 설정: ${Math.max(configuredInterval, 5)}분`);
    
    const interval = setInterval(() => {
      console.log('📰 정기 뉴스 업데이트 실행');
      fetchNews(false, 1, false); // 첫 페이지만 새로고침
    }, updateInterval);
    
    return () => {
      console.log('📰 뉴스 업데이트 interval 정리');
      clearInterval(interval);
    };
  }, [settings?.newsUpdateInterval, fetchNews]);

  // 다음 페이지 로드 함수
  const loadMoreNews = () => {
    if (currentPage < totalPages && !loadingMore) {
      fetchNews(false, currentPage + 1, true);
    }
  };

  // 수동 새로고침 함수 (진짜 새로고침 시에만 강제 RSS 수집)
  const refreshNews = async () => {
    setCurrentPage(1);
    setRefreshing(true); // loading 대신 refreshing 사용
    setError(null);
    
    try {
      const browserLanguage = navigator.language || navigator.languages?.[0] || 'en';
      const isKoreanUser = language === 'ko' || browserLanguage.startsWith('ko');
      const newsLanguage = isKoreanUser ? 'korean' : 'english';
      
      // 수동 새로고침 시에만 refresh=true 사용
      const refreshUrl = `${getApiUrl()}/api/news?language=${newsLanguage}&page=1&limit=10&refresh=true`;
      
      console.log('📰 수동 새로고침 요청:', refreshUrl);
      
      const response = await fetch(refreshUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: AbortSignal.timeout(30000) // 새로고침은 30초 타임아웃
      });

      if (!response.ok) {
        throw new Error(`서버 응답 오류: ${response.status} ${response.statusText}`);
      }

      const data: NewsResponse = await response.json();
      
      if (data.status === 'ok' && data.articles && Array.isArray(data.articles)) {
        const validatedArticles = data.articles.filter((article: NewsArticle) => {
          return article.url && 
                 article.url !== '#' && 
                 article.url.startsWith('http') &&
                 article.title && 
                 article.title.trim() !== '' &&
                 article.title !== 'No title';
        });

        // 기존 뉴스를 유지하면서 새로운 뉴스 추가
        setNews(prev => {
          // 기존 뉴스의 URL들을 Set으로 만들어 중복 체크
          const existingUrls = new Set(prev.map(article => article.url));
          
          // 새로운 뉴스 중 중복되지 않는 것들만 필터링
          const newUniqueArticles = validatedArticles.filter(article => 
            !existingUrls.has(article.url)
          );
          
          // 새로운 뉴스를 맨 앞에 추가하고, 전체 개수가 100개를 초과하면 뒤에서 제거
          const updatedNews = [...newUniqueArticles, ...prev];
          return updatedNews.slice(0, 100); // 최대 100개 유지
        });
        
        setCurrentPage(1);
        setTotalPages(data.totalPages || 1);
        setTotalResults(data.totalResults || validatedArticles.length);
        setNewsInfo({
          language: data.language || newsLanguage,
          sources: data.sources || { rss: 0, api: 0 },
          updateInterval: data.updateInterval || 15
        });
        setLastUpdated(new Date());
        console.log(`📰 수동 새로고침 완료: ${validatedArticles.length}개 새 뉴스 추가 (총 ${data.totalResults}개)`);
      } else {
        throw new Error(data.message || '새로고침 실패');
      }
    } catch (error) {
      console.error('📰 수동 새로고침 실패:', error);
      const errorMessage = error instanceof Error ? error.message : '새로고침에 실패했습니다';
      setError(errorMessage);
    } finally {
      setRefreshing(false); // loading 대신 refreshing 사용
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const now = new Date();
    const publishedDate = new Date(dateString);
    const diffMinutes = Math.floor((now.getTime() - publishedDate.getTime()) / (1000 * 60));
    
    if (diffMinutes < 1) return language === 'ko' ? '방금 전' : 'Just now';
    if (diffMinutes < 60) return language === 'ko' ? `${diffMinutes}분 전` : `${diffMinutes}m ago`;
    
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return language === 'ko' ? `${diffHours}시간 전` : `${diffHours}h ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    return language === 'ko' ? `${diffDays}일 전` : `${diffDays}d ago`;
  };

  const truncateText = (text: string, maxLength: number) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const handleArticleClick = (e: React.MouseEvent, article: NewsArticle) => {
    e.preventDefault();
    if (isValidUrl(article.url)) {
      window.open(article.url, '_blank', 'noopener,noreferrer');
    }
  };

  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url);
      return url.startsWith('http') && url !== '#';
    } catch {
      return false;
    }
  };

  const getSourceIcon = (sourceName: string) => {
    // RSS 소스인지 API 소스인지 구분
    const rssKeywords = ['코인데스크', '블록미디어', 'coindesk', 'cointelegraph', 'decrypt', 'the block'];
    const isRssSource = rssKeywords.some(keyword => 
      sourceName.toLowerCase().includes(keyword.toLowerCase())
    );
    
    return isRssSource ? (
      <RssIcon className="h-3 w-3 text-orange-500 dark:text-orange-400" title="RSS 뉴스" />
    ) : (
      <CloudIcon className="h-3 w-3 text-blue-500 dark:text-blue-400" title="API 뉴스" />
    );
  };

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 shadow-md overflow-hidden">
      {/* 헤더 */}
      <div className="flex-shrink-0 bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="p-4 sm:p-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 dark:from-gray-600 dark:to-slate-700 rounded-lg shadow-md">
                <NewspaperIcon className="h-5 w-5 text-white" />
              </div>
              <div className="flex flex-col">
                <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 tracking-tight">실시간 최신 뉴스</h3>
                <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span>실시간 업데이트</span>
                  </div>
                  <span>•</span>
                  <div className="flex items-center gap-1">
                    <RssIcon className="h-3 w-3 text-orange-500" />
                    <span>{newsInfo.sources.rss}</span>
                    <CloudIcon className="h-3 w-3 text-blue-500 ml-1" />
                    <span>{newsInfo.sources.api}</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-gray-200 dark:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-600">
                <ClockIcon className="h-3 w-3 text-gray-600 dark:text-gray-300" />
                <span className="text-xs text-gray-600 dark:text-gray-300">
                  {lastUpdated ? formatTimeAgo(lastUpdated.toISOString()) : '업데이트 중...'}
                </span>
              </div>
              <button
                onClick={refreshNews}
                disabled={refreshing}
                className="group relative flex items-center justify-center w-9 h-9 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                title="뉴스 새로고침"
              >
                <ArrowPathIcon className={`h-4 w-4 text-gray-600 dark:text-gray-300 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-all duration-200 ${refreshing ? 'animate-spin' : 'group-hover:rotate-12'}`} />
                
                {/* 새로고침 중일 때 미묘한 펄스 효과 */}
                {refreshing && (
                  <div className="absolute inset-0 bg-blue-50 dark:bg-blue-900/20 rounded-lg animate-pulse"></div>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 통계 정보 바 */}
      <div className="px-4 sm:px-5 py-3 bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-4 text-gray-600 dark:text-gray-300">
            <div className="flex items-center gap-1">
              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
              <span>언어: {newsInfo.language === 'korean' ? '한국어' : 'English'}</span>
            </div>
            <div className="hidden sm:flex items-center gap-1">
              <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
              <span>{newsInfo.updateInterval}분마다 갱신</span>
            </div>
          </div>
          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
            <span>총 {totalResults}개</span>
            <span className="text-gray-500 dark:text-gray-400">•</span>
            <span>{currentPage}/{totalPages} 페이지</span>
          </div>
        </div>
      </div>

      {/* 뉴스 목록 */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-40 text-gray-600 dark:text-gray-300">
            <div className="relative">
              <div className="w-8 h-8 border-2 border-gray-300 dark:border-gray-600 border-t-blue-500 rounded-full animate-spin"></div>
            </div>
            <p className="mt-3 text-sm">뉴스를 불러오는 중...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-40 text-center px-4">
            <ExclamationTriangleIcon className="h-12 w-12 text-red-500 dark:text-red-400 mb-3" />
            <p className="text-red-600 dark:text-red-400 text-sm mb-4 max-w-sm">{error}</p>
            <button
              onClick={() => fetchNews()}
              className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 dark:from-gray-600 dark:to-slate-700 dark:hover:from-gray-700 dark:hover:to-slate-800 text-white rounded-lg transition-all duration-200 text-sm font-medium shadow-lg"
            >
              다시 시도
            </button>
          </div>
        ) : (
          <div className="p-3 sm:p-4 space-y-2">
            {news.length > 0 ? (
              news.map((article, index) => (
                <div
                  key={index}
                  onClick={(e) => handleArticleClick(e, article)}
                  className="group bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 rounded-lg p-3 border border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 transition-all duration-200 cursor-pointer hover:shadow-md"
                >
                  <div className="flex flex-col gap-2">
                    {/* 뉴스 제목 - 폰트 사이즈 한 포인트 더 증가 */}
                    <h4 className="text-gray-800 dark:text-gray-100 font-medium text-lg leading-snug line-clamp-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                      {article.title}
                    </h4>
                    
                    {/* AI 요약 또는 뉴스 설명 - 2줄로 변경, 폰트 크기 증가 */}
                    {(article.aiSummary || article.description) && (
                      <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed line-clamp-2 opacity-90">
                        {article.aiSummary 
                          ? truncateText(article.aiSummary, 180)
                          : truncateText(article.description, 150)
                        }
                      </p>
                    )}
                    
                    {/* 하단 정보 - 더 간소화 */}
                    <div className="flex items-center justify-between pt-1">
                      <div className="flex items-center gap-2">
                        {getSourceIcon(article.source.name || '')}
                        <span className="text-xs text-gray-600 dark:text-gray-300 font-medium">
                          {truncateText(article.source.name || 'Unknown', 15)}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          •
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {formatTimeAgo(article.publishedAt)}
                        </span>
                      </div>
                      
                      {/* 읽기 텍스트 제거하고 화살표만 */}
                      <ArrowTopRightOnSquareIcon className="h-3 w-3 text-gray-500 dark:text-gray-400 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors" />
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex flex-col items-center justify-center h-32 text-gray-600 dark:text-gray-300">
                <NewspaperIcon className="h-12 w-12 mb-3 opacity-50" />
                <p className="text-sm">표시할 뉴스가 없습니다</p>
              </div>
            )}
            
            {/* 더 많은 뉴스 로드 버튼 */}
            {currentPage < totalPages && !loading && (
              <div className="flex justify-center pt-3">
                <button
                  onClick={loadMoreNews}
                  disabled={loadingMore}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 border border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 rounded-lg transition-all duration-200 disabled:opacity-50 text-sm font-medium"
                >
                  {loadingMore ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-gray-400 dark:border-gray-400 border-t-blue-500 dark:border-t-blue-400 rounded-full animate-spin"></div>
                      <span>로딩 중...</span>
                    </div>
                  ) : (
                    '더 많은 뉴스 보기'
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RealTimeNews; 